import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import SupportIcon from "../../../assets/SupportIcon";
import { Button } from "../../Catalyst/button";
import { Divider } from "../../Catalyst/divider";
import { Input, InputGroup } from "../../Catalyst/input";
import StatCard from "../../common/StatCard";
import UserListTable from "../../common/UserListTable";
import React, { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import useOrgId from "../../../hooks/useOrgId";
import { AppDispatch, RootState } from "../../../store/store";
import UsersTable from "../UsersTable/UsersTable";
import { fetchOrganizationDetailsAsync, fetchOrganizationSummaryAsync } from "../../../store/thunks/OrganizationThunk";
import { fetchUserStatsAsync } from "../../../store/thunks/OrganizationThunk";
import InviteUserIcon from "../../../assets/InviteUserIcon";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "../../Catalyst/dialog";
import { Field, Label } from "../../Catalyst/fieldset";
import { SendManuallyComponent } from "./SendManuallyComponent";
import { UploadCSVComponent } from "./UploadCSVComponent";
import FinleyIcon from "../../../assets/FinleyIcon ";
import FinleyIconName from "../../../assets/FinleyIconName";

interface UsersTableProps {
  selectedOrgId: any | undefined;
  searchText?: string | undefined;
}

const UserListDashboard = () => {

  const [searchText, setSearchText] = useState<string>();
  // Removed AllUserStats local state; use Redux instead
  // const selectedParentOrgId = useSelector((state: RootState) => state.OrganizationListStore?.selectedParentOrgId) ?? undefined;
  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);
  const orgSummary = useSelector((state: RootState) => state.OrganizationListStore?.orgSummary);

  const orgId = useOrgId();
  const dispatch = useDispatch<AppDispatch>();
  const isFinancialCoach = userAccount?.user_role === 'FINANCIAL_COACH';



  useEffect(() => {
    if (orgId) {
      dispatch(fetchOrganizationSummaryAsync(orgId));
    } else {
      dispatch(fetchUserStatsAsync());
    }
  }, [orgId, dispatch]);

  useEffect(() => {
    if (orgId) {
      dispatch(fetchOrganizationDetailsAsync(orgId));
    }
  }, [orgId, dispatch]);
  const orgDetails = useSelector((state: RootState) => state.OrganizationListStore?.selectedOrgDetails);
  const allUserStats = useSelector((state: RootState) => state.OrganizationListStore?.allUserStats); // Assumes this is where user stats are stored

  const debouncedSetSearchText = useCallback(
    debounce((value: string) => {
      setSearchText(value); // Update searchText state after delay
    }, 100),
    [setSearchText]
  );
  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    debouncedSetSearchText(query); // Debounce the setSearchText function
  };
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
// console.log("orgDetails", allUserStats);
  return (
    <div className="p-8">
      <div className="flex  justify-between">
        <div className="desktext10 ">
          {orgId && orgDetails?.name
            ? `Welcome back, ${orgDetails.name}`
            : 'Welcome back'}
        </div>
        <Button
          outline
          onClick={() => setIsSupportModalOpen(true)}
          aria-label="Open support form"
        >
          <SupportIcon />
          Support
        </Button>
      </div>


      {!isFinancialCoach && (
        <div className=" flex mt-[22px] gap-5">
          <StatCard
            title="Active Users"
            description={`Total active users at ${orgSummary?.displayName || 'organization'}`}
            value={orgId ? orgSummary?.activeUsers : allUserStats?.totalActiveUsers}
            valueLabel="active users"
          />
          <StatCard
            title="Pending Invites"
            description="Total outstanding invitations"
            value={orgId ? orgSummary?.invitedUsers : allUserStats?.totalPendingUsers}
            valueLabel=" pending invites"
          />
        </div>
      )}

      
      <div className="h-100vdh mt-5 w-full rounded-lg border-2 border-shadowborder pt-4">
{   orgId &&    <>
        <div className="flex justify-between items-center px-6 pb-6">
          <div>
            <div className="desktext11  text-texthead">Your Invited Users</div>
            <div className="font-inter text-sm font-normal leading-5 text-text-tertiary-600 mt-1">Find users you've invited</div>
          </div>
          {!isFinancialCoach && (
            <div>

              <Button onClick={() => setIsInviteDialogOpen(true)}>
                <InviteUserIcon /> Invite User
              </Button>

            </div>
          )}


        </div>
        <div className="w-[490px] pl-6">
          <InputGroup >
            <MagnifyingGlassIcon />
            <Input onChange={handleChange} name="search" placeholder="Search by user’s name, email or Unique Id" aria-label="Search" />
          </InputGroup>

        </div>
        </>}
        <div className="pt-6">
          <UserListTable searchText={searchText} />
        </div>
      </div>
      <Dialog size="5xl" className="" open={isInviteDialogOpen} onClose={setIsInviteDialogOpen}>
        <div className="flex w-full mt-[25px] items-center   ">
          <div className=" rounded-lg bg-white w-full  shadow-sm">
            <div className="mb-6">
         
              <Button outline className="h-10 w-18" onClick={() => setIsInviteDialogOpen(false)}>
                Close
              </Button>
            </div>

            <div className="mb-4 flex items-center gap-2">
              <FinleyIconName/>
            </div>

            <h2 className="mb-4 desktext14 text-gray-900">Invite users to {orgDetails?.name} with CSV Upload</h2>

            <UploadCSVComponent />

            <div className="my-6 text-start text-sm text-gray-500">OR</div>

            <SendManuallyComponent />
          </div>
        </div>
      </Dialog>

      {/* Support Modal with Tally Form */}
      <Dialog size="5xl" open={isSupportModalOpen} onClose={setIsSupportModalOpen}>
        <div className="flex justify-between items-center">
          <div>
            <DialogTitle>Support</DialogTitle>
            <DialogDescription>
              Please fill out the form below and we'll get back to you as soon as possible.
            </DialogDescription>
          </div>
          <Button
            plain
            onClick={() => setIsSupportModalOpen(false)}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </Button>
        </div>
        <DialogBody className="p-0">
          <div className="w-full h-[650px] overflow-hidden">
            <iframe 
              src="https://tally.so/embed/wgkQ8K?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              marginHeight={0} 
              marginWidth={0} 
              title="Support Form"
              className="border-none"
              style={{
                margin: 0,
                padding: 0,
                overflow: 'hidden'
              }}
            />
          </div>
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setIsSupportModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default UserListDashboard;
